import { isErrorCode } from '@lead-bridge/shared';
import en from '../locales/en.json';
import uz from '../locales/uz.json';
import ru from '../locales/ru.json';

type Locale = 'en' | 'uz' | 'ru';
const DICT: Record<Locale, Record<string, string>> = { en, uz, ru };

function activeLocale(): Locale {
  if (typeof window === 'undefined') return 'uz';
  const l = localStorage.getItem('lb-locale');
  return l === 'en' || l === 'uz' || l === 'ru' ? l : 'uz';
}

function lookup(key: string): string {
  const loc = activeLocale();
  return DICT[loc]?.[key] ?? DICT.en[key] ?? key;
}

/** Translate a single message string. If it's an `errors.*` code → translate; otherwise return as-is. */
export function translateMessage(msg: string | null | undefined): string {
  if (!msg) return lookup('errors.unknown');
  if (isErrorCode(msg)) return lookup(msg);
  // Heuristic: tRPC may surface a JSON-stringified zod issue array. Pick the first message.
  if (msg.startsWith('[')) {
    try {
      const arr = JSON.parse(msg) as Array<{ message?: string }>;
      const first = arr.find((i) => i?.message)?.message;
      if (first) return isErrorCode(first) ? lookup(first) : first;
    } catch {
      // fall through
    }
  }
  return msg;
}

interface TRPCErrorish {
  message?: string;
  data?: { zod?: { fieldErrors?: Record<string, string[]>; formErrors?: string[] } | null };
}

/** Translate a tRPC / Error / unknown error into a single user-facing string. */
export function translateError(err: unknown): string {
  if (!err) return lookup('errors.unknown');
  const e = err as TRPCErrorish;
  // Prefer the first zod field error if present.
  const zod = e.data?.zod;
  if (zod) {
    const fieldMsg = zod.fieldErrors
      ? Object.values(zod.fieldErrors)
          .flat()
          .find((m): m is string => typeof m === 'string')
      : undefined;
    const formMsg = zod.formErrors?.[0];
    const msg = fieldMsg ?? formMsg;
    if (msg) return translateMessage(msg);
  }
  if (typeof e.message === 'string') return translateMessage(e.message);
  if (err instanceof Error) return translateMessage(err.message);
  return lookup('errors.unknown');
}
